import apiClient from './api';

// ==========================================
// TYPE DEFINITIONS
// ==========================================

/**
 * Learning Path with Progress (List view)
 */
export interface LearningPathWithProgress {
  learning_path_id: string;
  tracking_id: string;
  title: string;
  description: string;
  banner_image_url?: string;
  thumbnail_image_url?: string;
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  progress_percentage: number;
  total_courses: number;
  completed_courses: number;
  total_time_spent_seconds: number;
  deadline_date?: string;
  last_accessed_at?: string;
  is_mandatory: boolean;
  started_date?: string;
  completed_date?: string;
  rating?: number;
  review_count?: number;
  certificate_number?: string;
}

/**
 * Course Progress within Learning Path
 */
export interface CourseProgress {
  tracking_id: string;
  course_id: string;
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'EXPIRED';
  progress_percentage: number;
  completed_modules: number;
  total_modules: number;
  started_at?: string;
  completed_at?: string;
  last_accessed_at?: string;
  time_spent_seconds: number;
}

/**
 * Course in Path (from LearningPath definition)
 */
export interface CourseInPath {
  course_id: string;
  order: number;
  course_title: string;
  course_description?: string;
  difficulty_level?: string;
  rationale: string;
}

/**
 * Learning Path Detail (Detail view)
 */
export interface LearningPathDetail {
  // Tracking Information
  tracking_id: string;
  assignment_id: string;
  user_id: string;
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  started_date?: string;
  last_accessed_at?: string;
  completed_date?: string;
  is_mandatory: boolean;
  deadline_date?: string;

  // Learning Path Details
  learning_path_id: string;
  title: string;
  description: string;
  banner_image_url?: string;
  thumbnail_image_url?: string;
  tags: string[];
  outcomes?: string[];
  difficulty_progression?: string;

  // Ordered courses from learning path definition
  courses: CourseInPath[];

  // Progress Details
  total_courses: number;
  completed_courses: number;
  progress_percentage: number;
  total_time_spent_seconds: number;

  // Course Progress (individual course tracking)
  course_progress: CourseProgress[];

  // Metadata
  created_at: string;
  updated_at: string;
}

/**
 * Dashboard Stats
 */
export interface DashboardStats {
  total_learning_paths: number;
  completed_learning_paths: number;
  in_progress_learning_paths: number;
  mandatory_learning_paths: number;
  total_time_spent_hours: number;
  average_progress: number;
  upcoming_deadlines?: Array<{
    learning_path_id: string;
    learning_path_title: string;
    deadline_date: string;
  }>;
  recent_activity?: Array<{
    learning_path_id: string;
    learning_path_title: string;
    last_accessed_at: string;
  }>;
}

// ==========================================
// LEARNER LEARNING PATH SERVICE
// ==========================================

class LearnerLearningPathService {
  private baseUrl = '/learner/learning-paths';

  /**
   * Get all learning paths for logged-in user
   * GET /api/learner/learning-paths
   */
  async getMyLearningPaths(): Promise<LearningPathWithProgress[]> {
    try {
      const response = await apiClient.get<{
        success: boolean;
        data: LearningPathWithProgress[];
      }>(this.baseUrl);

      if (!response.data.success) {
        throw new Error('Failed to fetch learning paths');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching learning paths:', error);
      throw new Error(
        error?.response?.data?.message || 'Failed to fetch learning paths'
      );
    }
  }

  /**
   * Get learning path detail with course progress
   * GET /api/learner/learning-paths/:id
   */
  async getLearningPathDetail(learning_path_id: string): Promise<LearningPathDetail> {
    try {
      const response = await apiClient.get<{
        success: boolean;
        data: LearningPathDetail;
      }>(`${this.baseUrl}/view/${learning_path_id}`);

      if (!response.data.success) {
        throw new Error('Failed to fetch learning path details');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching learning path details:', error);
      throw new Error(
        error?.response?.data?.message || 'Failed to fetch learning path details'
      );
    }
  }

  /**
   * Get dashboard stats for learning paths
   * GET /api/learner/learning-paths/dashboard
   */
  async getDashboardStats(): Promise<DashboardStats> {
    try {
      const response = await apiClient.get<{
        success: boolean;
        data: DashboardStats;
      }>(`${this.baseUrl}/dashboard`);

      if (!response.data.success) {
        throw new Error('Failed to fetch dashboard stats');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching dashboard stats:', error);
      throw new Error(
        error?.response?.data?.message || 'Failed to fetch dashboard stats'
      );
    }
  }

  /**
   * Utility: Format time spent in human-readable format (hh:mm:ss)
   */
  formatTimeSpent(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    // Format with leading zeros
    const hh = String(hours).padStart(2, '0');
    const mm = String(minutes).padStart(2, '0');
    const ss = String(secs).padStart(2, '0');

    return `${hh}:${mm}:${ss}`;
  }

  /**
   * Utility: Calculate days remaining until deadline
   */
  getDaysRemaining(deadline_date: string): number | null {
    if (!deadline_date) return null;
    const deadline = new Date(deadline_date);
    const now = new Date();
    const diff_ms = deadline.getTime() - now.getTime();
    return Math.ceil(diff_ms / (1000 * 60 * 60 * 24));
  }

  /**
   * Utility: Check if deadline is overdue
   */
  isOverdue(deadline_date: string): boolean {
    if (!deadline_date) return false;
    const deadline = new Date(deadline_date);
    const now = new Date();
    return deadline < now;
  }
}

export const learnerLearningPathService = new LearnerLearningPathService();
